import React, { useEffect, useRef, useState } from "react";
import { useSearchEngine } from "../hooks";
import { Facet } from "../shared.models";
import {
  selectIsLoading,
  selectSearchesPerformedCount,
} from "../store/shared.reducer";
import { useAppSelector } from "../store/store.hooks";
import { Popper } from "./Popper";

interface Props {
  config: Facet;
}
export const ToggleList: React.VFC<Props> = ({ config }) => {
  /*********************************
   * Variables
   *********************************/
  const { displayName, filters } = config;
  const ref = useRef<HTMLButtonElement>(null);

  const getHelpTextHeading = (id: string) => {
    switch (id) {
      case "lokala":
        return "Lokala dokument";
      case "overgripande":
        return "Övergripande dokument";
      case "regionovergripande":
        return "Regionövergripande dokument";
      case "vardhandboken":
        return "Vårdhandboken";
      default:
        return "";
    }
  };

  /*********************************
   * Render
   *********************************/
  return (
    <form method="get" className="vgr-togglelist">
      <div className="vgr-togglelist__heading">{displayName}</div>
      <ul>
        {filters.map((facet) => (
          <Item
            key={`includes_facet_${facet.id}`}
            facet={facet}
            helpTextHeading={getHelpTextHeading(facet.id)}
          />
        ))}
      </ul>

      <noscript className="vgr-searchservice__apply-includes-filter">
        <button ref={ref} className="vgr-searchservice__button" type="submit">
          Applicera filter
        </button>
      </noscript>
    </form>
  );
};

const Item = (props: { facet: Facet; helpTextHeading: string }) => {
  /*********************************
   * Variables
   *********************************/
  const { facet, helpTextHeading } = props;
  const isLoading = useAppSelector(selectIsLoading);
  const searchesPerformedCount = useAppSelector(selectSearchesPerformedCount);
  const pathName =
    typeof window !== "undefined" ? window.location.pathname : ""; // En tillfällig fix. Sökningen kommer inte att fungera utan javascript, eftersom vi behöver sökvägen till sidan.
  const [doSearch] = useSearchEngine(pathName + "/FilterStyrandeDokument");

  const ref = useRef<HTMLInputElement>(null);
  const helpButtonRef = useRef<HTMLButtonElement>(null);
  const [showHelp, setShowHelp] = useState(false);

  /*********************************
   * Functions
   *********************************/
  const onChange = (value: string) => doSearch(value);

  const checkboxKeyDown = (e: React.KeyboardEvent<HTMLLabelElement>) => {
    if (e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      ref.current.click();
    }
  };

  // Sync server response with local state
  useEffect(() => {
    const isCurrentlySelected = ref.current.checked;
    if (isCurrentlySelected !== facet.selected) {
      console.warn(
        "ToggleList mismatch detected - re-syncing with server data"
      );
      ref.current.checked = facet.selected;
    }
  }, [searchesPerformedCount, facet]);

  /*********************************
   * Render
   *********************************/
  return (
    <li>
      <div className="vgr-togglelist__textwrapper">
        <div className="vgr-togglelist__text">
          <div className="vgr-togglelist__info">
          {facet.displayName} ({facet.count}){" "}</div>
          <button
            onClick={() => setShowHelp(!showHelp)}
            ref={helpButtonRef}
            type="button"
            className="vgr-togglelist__helpbtn"
            aria-label={`Vad är ${facet.displayName}? Aktivera för mer information`}
          >
            {helpIcon}
          </button>
        </div>
        <div className="vgr-togglelist__subtext">{facet.subtitle}</div>
      </div>
      <Popper
        show={showHelp}
        setShow={setShowHelp}
        anchor={helpButtonRef}
        content={facet.helpText}
        helpTextHeading={helpTextHeading}
      />
      <label
        className="vgr-switch"
        onKeyDown={checkboxKeyDown}
        tabIndex={0}
        role="checkbox"
        aria-checked={ref.current?.checked}
      >
        <span className="sr-only">
          {facet.displayName}, {facet.count} dokument
        </span>
        <input
          ref={ref}
          disabled={isLoading}
          type="checkbox"
          tabIndex={-1}
          defaultChecked={facet.selected}
          name={facet.displayName}
          value={facet.query}
          onChange={() => onChange(facet.query)}
        />
        <span className="vgr-switch__ui"></span>
      </label>
    </li>
  );
};

const helpIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
<g clipPath="url(#clip0_13076_1362)">
<path d="M14.5 8C14.5 6.27609 13.8152 4.62279 12.5962 3.40381C11.3772 2.18482 9.72391 1.5 8 1.5C6.27609 1.5 4.62279 2.18482 3.40381 3.40381C2.18482 4.62279 1.5 6.27609 1.5 8C1.5 9.72391 2.18482 11.3772 3.40381 12.5962C4.62279 13.8152 6.27609 14.5 8 14.5C9.72391 14.5 11.3772 13.8152 12.5962 12.5962C13.8152 11.3772 14.5 9.72391 14.5 8ZM0 8C0 5.87827 0.842855 3.84344 2.34315 2.34315C3.84344 0.842855 5.87827 0 8 0C10.1217 0 12.1566 0.842855 13.6569 2.34315C15.1571 3.84344 16 5.87827 16 8C16 10.1217 15.1571 12.1566 13.6569 13.6569C12.1566 15.1571 10.1217 16 8 16C5.87827 16 3.84344 15.1571 2.34315 13.6569C0.842855 12.1566 0 10.1217 0 8ZM5.30625 5.16563C5.55312 4.46875 6.21563 4 6.95625 4H8.77812C9.86875 4 10.75 4.88438 10.75 5.97188C10.75 6.67813 10.3719 7.33125 9.75937 7.68437L8.75 8.2625C8.74375 8.66875 8.40938 9 8 9C7.58437 9 7.25 8.66562 7.25 8.25V7.82812C7.25 7.55937 7.39375 7.3125 7.62813 7.17812L9.0125 6.38438C9.15938 6.3 9.25 6.14375 9.25 5.975C9.25 5.7125 9.0375 5.50313 8.77812 5.50313H6.95625C6.85 5.50313 6.75625 5.56875 6.72188 5.66875L6.70937 5.70625C6.57188 6.09688 6.14062 6.3 5.75313 6.1625C5.36563 6.025 5.15938 5.59375 5.29688 5.20625L5.30937 5.16875L5.30625 5.16563ZM7 11C7 10.7348 7.10536 10.4804 7.29289 10.2929C7.48043 10.1054 7.73478 10 8 10C8.26522 10 8.51957 10.1054 8.70711 10.2929C8.89464 10.4804 9 10.7348 9 11C9 11.2652 8.89464 11.5196 8.70711 11.7071C8.51957 11.8946 8.26522 12 8 12C7.73478 12 7.48043 11.8946 7.29289 11.7071C7.10536 11.5196 7 11.2652 7 11Z" fill="#005B89"/>
</g>
<defs>
<clipPath id="clip0_13076_1362">
<rect width="16" height="16" fill="white"/>
</clipPath>
</defs>
</svg>
);
